import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { Download as DownloadIcon } from "lucide-mui";
import { useState, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { keys } from "../../../api/queryKeys.ts";
import { api, type TableStats } from "../../../api/client.ts";
import ChipAutocomplete from "../../../components/ChipAutocomplete.tsx";
import { getCountryFlag, getCountryName } from "../../../components/CountryName.tsx";
import { useFilterOptions } from "../../../hooks/useFilterOptions.ts";
import type { SortDir } from "../../../hooks/useUrlState.ts";
import Panel from "../../../components/Panel.tsx";
import SectionHeading from "../../../components/SectionHeading.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import SortableTableHead, { type SortableColumn } from "../../../components/table/SortableTableHead.tsx";
import TableCard from "../../../components/table/TableCard.tsx";
import { formatNumber } from "../../../utils/format.ts";
import PageHeader from "../../../components/PageHeader.tsx";

type SortKey = keyof TableStats;

const COLUMNS: SortableColumn[] = [
  { id: "table_name", label: "Table" },
  { id: "row_count", label: "Records", align: "right" },
  { id: "size_mb", label: "Size (MB)", align: "right" },
];

function sorted(rows: TableStats[], key: SortKey, dir: SortDir): TableStats[] {
  return [...rows].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    const cmp = typeof av === "string" ? av.localeCompare(bv as string) : (av as number) - (bv as number);
    return dir === "asc" ? cmp : -cmp;
  });
}

function DatabasePage() {
  const [sortKey, setSortKey] = useState<SortKey>("table_name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);

  const { data: tables, isLoading } = useQuery({
    queryKey: keys.database.tableStats(),
    queryFn: api.getTableStats,
  });

  const { data: filterOptions } = useFilterOptions();

  const countryOptions = useMemo(
    () =>
      (filterOptions?.countries ?? []).map((code) => {
        const flag = getCountryFlag(code);
        const name = getCountryName(code);
        return { code, label: flag ? `${flag} ${name}` : name };
      }),
    [filterOptions?.countries],
  );

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const exportMissingWebsites = useMutation({
    mutationFn: () => api.exportCompaniesMissingWebsite(selectedCountries),
  });

  const sortedTables = tables ? sorted(tables, sortKey, sortDir) : [];
  const totalRows = tables?.reduce((sum, t) => sum + t.row_count, 0) ?? 0;
  const totalSize = tables?.reduce((sum, t) => sum + t.size_mb, 0) ?? 0;

  return (
    <>
      <PageHeader
        title="Database"
        description="Record counts and storage size for each table"
      />
      <TableCard loading={isLoading}>
        <ScrollTable>
          <SortableTableHead
            columns={COLUMNS}
            sortBy={sortKey}
            sortDir={sortDir}
            onSort={(column) => handleSort(column as SortKey)}
            defaultDir="asc"
          />
          <TableBody>
            {sortedTables.map((t: TableStats) => (
              <TableRow key={t.table_name}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {t.table_name}
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatNumber(t.row_count)}</TableCell>
                <TableCell align="right">{t.size_mb.toFixed(2)}</TableCell>
              </TableRow>
            ))}
            {tables && tables.length > 0 && (
              <TableRow sx={{ "& td": { fontWeight: 700 } }}>
                <TableCell>Total</TableCell>
                <TableCell align="right">{formatNumber(totalRows)}</TableCell>
                <TableCell align="right">{totalSize.toFixed(2)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </ScrollTable>
      </TableCard>

      <Box sx={{ mt: 4 }}>
        <SectionHeading title="Data exports" />
        <Panel
          title="Companies missing website"
          subtitle={
            <>
              Developer export for offline enrichment work: companies with no website. Norwegian
              companies are limited to AS, ANS, and ASA legal forms. Output is Parquet (Snappy
              compressed).
            </>
          }
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ minWidth: 300, flex: 1, maxWidth: 500 }}>
              <ChipAutocomplete
                label="Country"
                options={countryOptions}
                selected={selectedCountries}
                onChange={setSelectedCountries}
                placeholder="All countries"
              />
            </Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              loading={exportMissingWebsites.isPending}
              loadingPosition="start"
              onClick={() => exportMissingWebsites.mutate()}
              sx={{ mt: 0.5 }}
            >
              Export (.parquet)
            </Button>
          </Box>
        </Panel>
      </Box>
    </>
  );
}

export default DatabasePage;
